import { readEnv } from "@repo/helpers";
import {
    fetchAllowedChanges,
    LocalStorageKeys,
    selectIsLoaded,
    selectIsLoading,
    useAppDispatch,
    useAppSelector,
} from "@repo/logic";
import { type QueryCacheNotifyEvent, useQueryClient } from "@tanstack/react-query";
import { isNil } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { type GetDPSDetailResponse, type GetPPDetailResponse, ProductType, StarPensionClientTypeEnum } from "@repo/api";
import { type PensionContractType } from "@repo/logic";

export const AMLCheckIntervalMs =
    readEnv("GATSBY_FEATURE_USE_AML_PROMPT_PERIOD_FROM_LIBRARY") === "true"
        ? parseInt(readEnv("GATSBY_AML_PERIOD_IN_MINUTES")! || "0") * 60 * 1000
        : 1000 * 60 * 60 * 2;

export const getLastAmlChecks = (): {
    aml: number | null;
    bankId: number | null;
} => {
    const lastAMLCheckString = localStorage.getItem(LocalStorageKeys.LAST_AML_CHECK);
    const lastBankIdCheckString = localStorage.getItem(LocalStorageKeys.LAST_BANK_ID_CHECK);
    return {
        aml: lastAMLCheckString ? parseInt(lastAMLCheckString) : null,
        bankId: lastBankIdCheckString ? parseInt(lastBankIdCheckString) : null,
    };
};

export const getIsTimeToPrompt = (prompt: AMLPromt) => {
    const lastChecks = getLastAmlChecks();
    const promptID = prompt.ID && (lastChecks.bankId === null || Date.now() - AMLCheckIntervalMs > lastChecks.bankId);
    const promptAML = prompt.AML && (lastChecks.aml === null || Date.now() - AMLCheckIntervalMs > lastChecks.aml);
    return { promptID, promptAML };
};

export type AMLPromt = {
    ID: boolean;
    AML: boolean;
    contractType: PensionContractType;
    contractId: string;
};

const getContractTypeFromQueryKey = (queryKey: readonly unknown[]): PensionContractType | null => {
    const isKnownType = (value: unknown): value is PensionContractType =>
        value === ProductType.Uf || value === ProductType.Pf;

    const found = queryKey.find(isKnownType);
    return found ?? null;
};

const toPromptContextItem = (
    queryKey: readonly unknown[],
    queryData: unknown,
): {
    contractId: string;
    contractType: PensionContractType;
    clientType: GetDPSDetailResponse["clientType"] | GetPPDetailResponse["clientType"];
} | null => {
    const hasContractDetailKey = queryKey.includes("getContractDetail");
    if (!hasContractDetailKey) {
        return null;
    }

    const contractType = getContractTypeFromQueryKey(queryKey);
    if (!contractType || !queryData) {
        return null;
    }

    const data = queryData as GetDPSDetailResponse | GetPPDetailResponse;
    const contractId = data.contract?.contractNumber;
    if (!contractId) {
        return null;
    }

    const isChildrenContract = "isChildrenContract" in data && data.isChildrenContract;
    const isRetired = !isNil(data.contract.retirementDate);
    if (isRetired || isChildrenContract) {
        return null;
    }

    return {
        contractId,
        contractType,
        clientType: data.clientType,
    };
};

const filterQueryEvent = (event: QueryCacheNotifyEvent): boolean => {
    if (event.type !== "updated" || event.action.type !== "success") return false;
    return toPromptContextItem(event.query.queryKey, event.query.state.data) !== null;
};

const findAMLPrompt = (data: AMLData) => {
    const contractKeys = Object.keys(data);
    const keyWithBankIdPrompt = contractKeys.find((key) => data[key].ID);
    if (keyWithBankIdPrompt) return data[keyWithBankIdPrompt];
    const keyWithAMLPrompt = contractKeys.find((key) => data[key].AML);
    if (keyWithAMLPrompt) return data[keyWithAMLPrompt];
    return null;
};

type AMLData = Record<string, AMLPromt>;

type AMLPromptContext = Record<
    string,
    {
        contractId: string;
        contractType: PensionContractType;
        clientType: GetDPSDetailResponse["clientType"] | GetPPDetailResponse["clientType"];
    }
>;

export const useAMLPrompt = (): AMLPromt | null => {
    const dispatch = useAppDispatch();
    const client = useQueryClient();
    const cache = client.getQueryCache();
    const isAllowedChangesLoaded = useAppSelector(selectIsLoaded);
    const isAllowedChangesLoading = useAppSelector(selectIsLoading);
    const allowedChangesData = useAppSelector((state) => state.allowedChanges.data);
    const allowedChangesOverrides = useAppSelector((state) => state.allowedChanges.overrides);
    const [promptContext, setPromptContext] = useState<AMLPromptContext>({});

    useEffect(() => {
        if (!isAllowedChangesLoaded && !isAllowedChangesLoading) {
            dispatch(fetchAllowedChanges());
        }
    }, [dispatch, isAllowedChangesLoaded, isAllowedChangesLoading]);

    useEffect(() => {
        const entriesFromCache = cache.getAll().reduce<AMLPromptContext>((acc, query) => {
            const item = toPromptContextItem(query.queryKey, query.state.data);
            if (!item) {
                return acc;
            }

            acc[item.contractId] = item;
            return acc;
        }, {});

        if (Object.keys(entriesFromCache).length > 0) {
            setPromptContext((prev) => ({ ...prev, ...entriesFromCache }));
        }

        const deleteListener = cache.subscribe((event) => {
            if (filterQueryEvent(event)) {
                const item = toPromptContextItem(event.query.queryKey, event.query.state.data);
                if (!item) {
                    return;
                }

                setPromptContext((prev) => {
                    const existing = prev[item.contractId];
                    if (
                        existing?.contractType === item.contractType &&
                        existing?.clientType === item.clientType
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,
                        [item.contractId]: item,
                    };
                });
            }
        });
        return () => {
            deleteListener();
        };
    }, [cache]);

    return useMemo(() => {
        const amlData = Object.values(promptContext).reduce<AMLData>((acc, item) => {
            const allowedChanges = allowedChangesData[item.contractId];
            if (!allowedChanges) {
                return acc;
            }

            const contractOverrides = allowedChangesOverrides[item.contractId] ?? {};
            const isAmlFulfilled = contractOverrides.isAmlFulfilled ?? allowedChanges.isAmlFulfilled;
            const isBankIdIdentificationRequired =
                contractOverrides.isBankIdIdentificationRequired ??
                allowedChanges.isBankIdIdentificationRequired;

            acc[item.contractId] = {
                AML: isAmlFulfilled === false,
                ID:
                    isBankIdIdentificationRequired === true &&
                    item.clientType === StarPensionClientTypeEnum.Z,
                contractId: item.contractId,
                contractType: item.contractType,
            };

            return acc;
        }, {});

        return findAMLPrompt(amlData);
    }, [promptContext, allowedChangesData, allowedChangesOverrides]);
};
